/** @jsx jsx */
import React, { ReactNode } from 'react';
import { capitalize } from 'lodash';
import { css, jsx } from '@emotion/core';
import { Text } from '@zapier/design-system';

import {
  CreditCardBrandCode,
  PaymentDetailsBank,
  PaymentDetailsCard,
  PaymentSource,
  PaymentSourceType,
} from '@zapier/payment/src/types';

import { getCardDefinitionFromBrand } from '@zapier/payment/src/utils';
import PaymentMethodImage from '@zapier/payment/src/components/PaymentMethodImage';

const detailsCss = (isCompact = false, isInline = false) =>
  css(
    {
      display: 'flex',
      flexDirection: 'column',
      lineHeight: '18px',
      marginLeft: isCompact ? 10 : 15,
      padding: 0,
    },
    isInline && {
      alignItems: 'center',
      flexDirection: 'row',
    }
  );

const numbersCss = (isInline = false) => ({
  marginLeft: isInline ? 5 : 0,
});

const wrapperCss = {
  alignItems: 'center',
  display: 'flex',
};

type ChildrenProps = {
  descriptions: Descriptions;
  isInline?: boolean;
};

type Props = {
  children?: (a: ChildrenProps) => ReactNode;
  isCompact?: boolean;
  isInline?: boolean;
  /** Current payment source */
  paymentSource: PaymentSource;
};

type Descriptions = {
  imageType: string;
  default: {
    firstLine: string;
    secondLine: string;
  };
  compact: {
    firstLine: string;
    secondLine: string;
  };
};

const getPaymentDescriptions = (paymentSource: PaymentSource): Descriptions => {
  const method = paymentSource.method;
  switch (method) {
    case PaymentSourceType.card: {
      const cardDetails: PaymentDetailsCard = paymentSource.details as any;
      const definition = getCardDefinitionFromBrand(
        cardDetails.brand as CreditCardBrandCode
      );
      return {
        imageType: definition.type,
        default: {
          firstLine: `${definition.niceType} ending in ${cardDetails.last4}`,
          secondLine: `Expires ${cardDetails.expiry}`,
        },
        compact: {
          firstLine: `${definition.niceType}`,
          secondLine: `${cardDetails.last4}`,
        },
      };
    }

    case PaymentSourceType.bank: {
      const bankDetails: PaymentDetailsBank = paymentSource.details as any;
      return {
        imageType: 'ach',
        default: {
          firstLine: `${bankDetails.bankName} ${capitalize(
            bankDetails.accountType
          )}`,
          secondLine: `Ending in ${bankDetails.last4}`,
        },
        compact: {
          firstLine: `${capitalize(bankDetails.accountType)}`,
          secondLine: `${bankDetails.last4}`,
        },
      };
    }

    default:
      return {
        imageType: 'unknown',
        default: {
          firstLine: '',
          secondLine: '',
        },
        compact: {
          firstLine: '',
          secondLine: '',
        },
      };
  }
};

const renderDefaultInfo: React.FC<ChildrenProps> = ({ descriptions }) => {
  return (
    <React.Fragment>
      <Text fontWeight={700} type="smallPrint1">
        {descriptions.default.firstLine}
      </Text>
      <Text color="gray60" type="smallPrint1">
        {descriptions.default.secondLine}
      </Text>
    </React.Fragment>
  );
};

const renderCompactInfo: React.FC<ChildrenProps> = ({
  descriptions,
  isInline,
}) => (
  <React.Fragment>
    <Text fontWeight={700} type="smallPrint1">
      {descriptions.compact.firstLine}
    </Text>
    <span css={numbersCss(isInline)}>
      <Text color="gray60" type="smallPrint1">
        {descriptions.compact.secondLine}
      </Text>
    </span>
  </React.Fragment>
);

const PaymentInfo: React.FC<Props> = ({
  isCompact = false,
  isInline = false,
  children = isCompact ? renderCompactInfo : renderDefaultInfo,
  paymentSource,
}) => {
  const descriptions = getPaymentDescriptions(paymentSource);
  return (
    <div css={wrapperCss}>
      <PaymentMethodImage
        imageType={descriptions.imageType}
        isSmall={isCompact}
      />
      <div css={detailsCss(isCompact, isInline)}>
        {children({
          descriptions,
          isInline,
        })}
      </div>
    </div>
  );
};

export default PaymentInfo;
